import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  XCircle,
  HelpCircle,
  PenLine,
  ListChecks,
  RotateCcw,
  Sparkles,
  AlertTriangle,
  Timer,
} from 'lucide-react';
import { generateQuiz, gradeWritten, submitQuizResults } from '../api/quiz';
import { fetchSheets } from '../api/words';
import type { QuizQuestion } from '../types';
import type { QuizResult } from '../api/quiz';

const QUESTION_COUNTS = [5, 10, 15, 20, 25, 30, 50];
const TIMER_OPTIONS = [
  { label: 'Off', value: 0 },
  { label: '10s / q', value: 10 },
  { label: '20s / q', value: 20 },
  { label: '30s / q', value: 30 },
  { label: '60s / q', value: 60 },
];

export const QuizMode: React.FC = () => {
  const [mode, setMode] = useState<'mc' | 'written'>('mc');
  const [questionCount, setQuestionCount] = useState(10);
  const [customCount, setCustomCount] = useState('');
  const [selectedSheet, setSelectedSheet] = useState('');
  const [sheets, setSheets] = useState<{ sheet: string; count: number }[]>([]);
  const [timePerQuestion, setTimePerQuestion] = useState(0);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [results, setResults] = useState<QuizResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [serverXp, setServerXp] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    fetchSheets().then(setSheets).catch(() => {});
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startQuiz = useCallback(async () => {
    const count = customCount ? parseInt(customCount, 10) : questionCount;
    if (!count || count < 1) {
      setError('Please enter a valid question count');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const qs = await generateQuiz({
        mode,
        word_count: count,
        sheet: selectedSheet || undefined,
      });
      setQuestions(qs);
      setCurrentIndex(0);
      setScore(0);
      setResults([]);
      setStarted(true);
      setFinished(false);
      if (timePerQuestion > 0) {
        setTimeLeft(timePerQuestion);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load quiz questions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [mode, questionCount, customCount, selectedSheet, timePerQuestion]);

  useEffect(() => {
    if (started && timePerQuestion > 0 && !finished) {
      setTimeLeft(timePerQuestion);
      clearTimer();
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearTimer();
            // Auto-submit as incorrect
            const q = questions[currentIndex];
            if (q) {
              handleAnswer(false, timePerQuestion * 1000, 'Timed out');
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearTimer();
  }, [started, currentIndex, finished, timePerQuestion, questions.length]);

  const handleAnswer = useCallback((correct: boolean, responseTime: number, answer?: string) => {
    const q = questions[currentIndex];
    if (!q) return;
    // Guard against double submission (e.g. timer firing while result is shown)
    if (results.length > currentIndex) return;

    const result: QuizResult = {
      word_id: q.word_id,
      correct,
      response_time_ms: responseTime,
      answer,
    };

    const nextResults = [...results, result];
    setResults(nextResults);
    if (correct) setScore((s) => s + 1);

    if (currentIndex >= questions.length - 1) {
      clearTimer();
      submitQuizResults(nextResults, mode)
        .then((res) => setServerXp(res.xp_earned))
        .catch(() => {});
      setFinished(true);
    } else {
      setCurrentIndex((i) => i + 1);
    }
  }, [currentIndex, questions, results, clearTimer, mode]);

  const reset = useCallback(() => {
    clearTimer();
    setStarted(false);
    setFinished(false);
    setQuestions([]);
    setCurrentIndex(0);
    setScore(0);
    setResults([]);
    setError(null);
    setTimeLeft(0);
  }, [clearTimer]);

  if (!started) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-strong rounded-3xl p-8 md:p-10 max-w-md w-full"
          style={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.04) inset, 0 40px 80px rgba(0,0,0,0.6)' }}
        >
          <div className="text-center mb-6">
            <HelpCircle size={36} className="text-gold mx-auto mb-4 opacity-60" />
            <h2 className="font-display text-2xl text-cream mb-1">Quiz Mode</h2>
            <p className="text-sm text-text">Configure your quiz and test your knowledge.</p>
          </div>

          <div className="space-y-4">
            {/* Mode */}
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider text-text-dim mb-2">Mode</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setMode('mc')}
                  className={`py-2.5 rounded-xl border text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                    mode === 'mc'
                      ? 'border-gold-dim bg-gold-glow text-gold'
                      : 'border-border bg-surface text-text-dim hover:text-text-hi hover:bg-surface-1'
                  }`}
                >
                  <ListChecks size={16} />
                  Multiple Choice
                </button>
                <button
                  onClick={() => setMode('written')}
                  className={`py-2.5 rounded-xl border text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                    mode === 'written'
                      ? 'border-gold-dim bg-gold-glow text-gold'
                      : 'border-border bg-surface text-text-dim hover:text-text-hi hover:bg-surface-1'
                  }`}
                >
                  <PenLine size={16} />
                  Written
                </button>
              </div>
            </div>

            {/* Question count */}
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider text-text-dim mb-2">Questions</label>
              <div className="flex gap-2">
                <select
                  value={customCount ? '' : questionCount}
                  onChange={(e) => {
                    setQuestionCount(parseInt(e.target.value, 10));
                    setCustomCount('');
                  }}
                  className="flex-1 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold"
                >
                  {QUESTION_COUNTS.map((c) => (
                    <option key={c} value={c}>{c} questions</option>
                  ))}
                </select>
                <input
                  type="number"
                  value={customCount}
                  onChange={(e) => setCustomCount(e.target.value)}
                  placeholder="Custom"
                  min={1}
                  max={100}
                  className="w-24 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold placeholder:text-text-dim"
                />
              </div>
            </div>

            {/* Deck */}
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider text-text-dim mb-2">Deck</label>
              <select
                value={selectedSheet}
                onChange={(e) => setSelectedSheet(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold"
              >
                <option value="">All Words ({sheets.reduce((a, s) => a + s.count, 0)})</option>
                {sheets.map((s) => (
                  <option key={s.sheet} value={s.sheet}>
                    {s.sheet.startsWith('Practice') || s.sheet.startsWith('Pairs')
                      ? s.sheet
                      : `Sheet ${s.sheet}`} ({s.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Timer */}
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider text-text-dim mb-2">Timer</label>
              <div className="flex gap-2 flex-wrap">
                {TIMER_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setTimePerQuestion(opt.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      timePerQuestion === opt.value
                        ? 'border-gold-dim bg-gold-glow text-gold'
                        : 'border-border bg-surface text-text-dim hover:text-text-hi hover:bg-surface-1'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={startQuiz}
              disabled={loading}
              className="w-full py-3.5 rounded-xl btn-gold text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              ) : (
                <Sparkles size={16} />
              )}
              Start Quiz
            </button>
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                className="mt-5 flex items-start gap-3 p-4 rounded-xl bg-red-muted border border-red/30 text-left"
              >
                <AlertTriangle size={16} className="text-red shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red">{error}</p>
                  <button
                    onClick={() => setError(null)}
                    className="text-[11px] text-red/70 hover:text-red underline mt-1"
                  >
                    Dismiss
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    );
  }

  if (finished) {
    return (
      <QuizResults
        score={score}
        total={questions.length}
        results={results}
        xpEarned={serverXp}
        onRetry={reset}
      />
    );
  }

  const currentQuestion = questions[currentIndex];
  if (!currentQuestion) return null;

  return (
    <div className="h-full flex flex-col items-center justify-center px-4 md:px-8 py-4">
      {/* Progress + Timer */}
      <div className="w-full max-w-xl mb-6">
        <div className="flex justify-between text-[11px] text-text-dim font-medium mb-1.5">
          <span>Question {currentIndex + 1} / {questions.length}</span>
          <div className="flex items-center gap-3">
            <span>Score: {score}</span>
            {timePerQuestion > 0 && (
              <span className={`flex items-center gap-1 ${timeLeft <= 5 ? 'text-red' : 'text-gold'}`}>
                <Timer size={12} />
                {timeLeft}s
              </span>
            )}
          </div>
        </div>
        <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <motion.div
            className="h-full rounded-full bg-gold"
            style={{ boxShadow: '0 0 12px rgba(108,99,255,0.35)' }}
            animate={{ width: `${((currentIndex) / questions.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        {mode === 'mc' ? (
          <MCQuiz
            key={currentQuestion.word_id}
            question={currentQuestion}
            onAnswer={handleAnswer}
          />
        ) : (
          <WrittenQuiz
            key={currentQuestion.word_id}
            word={currentQuestion.word}
            wordId={currentQuestion.word_id}
            onAnswer={handleAnswer}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const MCQuiz: React.FC<{
  question: QuizQuestion;
  onAnswer: (correct: boolean, responseTime: number) => void;
}> = ({ question, onAnswer }) => {
  const [selected, setSelected] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const startTime = useRef<number>(Date.now());

  useEffect(() => {
    startTime.current = Date.now();
    setSelected(null);
    setShowResult(false);
  }, [question]);

  const handleSelect = (idx: number) => {
    if (selected !== null) return;
    setSelected(idx);
    setShowResult(true);
  };

  const handleContinue = () => {
    if (selected === null) return;
    onAnswer(question.options[selected].correct, Date.now() - startTime.current);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.25 }}
      className="w-full max-w-xl"
    >
      <div className="glass-card p-6 md:p-8 rounded-2xl mb-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim mb-4">Choose the correct definition</p>
        <h3 className="font-display text-2xl md:text-3xl text-cream text-center mb-2">{question.word}</h3>
      </div>

      <div className="space-y-2.5">
        {question.options.map((opt, i) => {
          const isSelected = selected === i;
          const isCorrect = opt.correct;
          let stateClasses = 'border-border bg-surface text-text hover:border-gold/40 hover:bg-violet-muted hover:text-text-hi hover:translate-x-1';

          if (showResult) {
            if (isCorrect) {
              stateClasses = 'border-green/50 bg-green-muted text-green animate-correctPulse';
            } else if (isSelected && !isCorrect) {
              stateClasses = 'border-red/50 bg-red-muted text-red';
            } else {
              stateClasses = 'border-border bg-surface text-text-dim opacity-60';
            }
          }

          return (
            <motion.button
              key={i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05, duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => handleSelect(i)}
              disabled={showResult}
              className={`w-full text-left px-4 md:px-5 py-3.5 rounded-xl border transition-all duration-150 text-sm leading-relaxed ${stateClasses}`}
            >
              {opt.text}
            </motion.button>
          );
        })}
      </div>

      {showResult && (
        <motion.button
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={handleContinue}
          autoFocus
          className="mt-4 btn-gold w-full py-3 rounded-xl text-sm font-semibold"
        >
          Continue ›
        </motion.button>
      )}
    </motion.div>
  );
};

interface WrittenQuizProps {
  word: string;
  wordId: string;
  onAnswer: (correct: boolean, responseTime: number, answer: string) => void;
}

const WrittenQuiz: React.FC<WrittenQuizProps> = ({ word, wordId, onAnswer }) => {
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState<{ correct: boolean; feedback: string; confidence: number } | null>(null);
  const [grading, setGrading] = useState(false);
  const startTime = useRef<number>(Date.now());

  useEffect(() => {
    startTime.current = Date.now();
    setAnswer('');
    setFeedback(null);
    setGrading(false);
  }, [word]);

  const handleSubmit = async () => {
    if (answer.trim().length < 3 || grading) return;
    setGrading(true);

    try {
      const result = await gradeWritten(wordId, answer);
      setFeedback(result);
    } catch {
      setFeedback({ correct: false, feedback: 'Could not grade response. The correct definition will be shown.', confidence: 0 });
    }
  };

  const handleContinue = () => {
    if (!feedback) return;
    onAnswer(feedback.correct, Date.now() - startTime.current, answer);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.25 }}
      className="w-full max-w-xl"
    >
      <div className="glass-card p-6 md:p-8 rounded-2xl mb-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim mb-4">Type the definition</p>
        <h3 className="font-display text-2xl md:text-3xl text-cream text-center mb-6">{word}</h3>

        <textarea
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Write your definition here..."
          rows={3}
          className="w-full bg-white/[0.04] border border-border rounded-xl px-4 py-3 text-sm text-cream placeholder:text-text-dim outline-none focus:border-gold transition-colors resize-none"
          disabled={!!feedback}
        />

        {!feedback && (
          <button
            onClick={handleSubmit}
            disabled={answer.trim().length < 3 || grading}
            className="mt-3 btn-gold w-full py-3 rounded-xl text-sm font-semibold disabled:opacity-50 disabled:cursor-default"
          >
            {grading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                Grading...
              </span>
            ) : (
              'Submit'
            )}
          </button>
        )}
      </div>

      <AnimatePresence>
        {feedback && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`glass-card p-5 rounded-2xl border ${
              feedback.correct
                ? 'border-green/30 bg-green-muted'
                : feedback.confidence >= 0.4
                ? 'border-amber/30 bg-amber-muted'
                : 'border-red/30 bg-red-muted'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              {feedback.correct ? (
                <CheckCircle2 size={18} className="text-green" />
              ) : (
                <XCircle size={18} className="text-red" />
              )}
              <span className={`text-sm font-semibold ${feedback.correct ? 'text-green' : 'text-red'}`}>
                {feedback.correct ? 'Correct!' : feedback.confidence >= 0.4 ? 'Partially Correct' : 'Incorrect'}
              </span>
            </div>
            <p className="text-sm text-text-hi leading-relaxed">{feedback.feedback}</p>
            <button
              onClick={handleContinue}
              autoFocus
              className="mt-4 btn-gold w-full py-3 rounded-xl text-sm font-semibold"
            >
              Continue ›
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const QuizResults: React.FC<{
  score: number;
  total: number;
  results: QuizResult[];
  xpEarned: number | null;
  onRetry: () => void;
}> = ({ score, total, xpEarned, onRetry }) => {
  const accuracy = total > 0 ? Math.round((score / total) * 100) : 0;
  // Server value when available; rough estimate while it loads / offline.
  const xpDisplay = xpEarned ?? score * 5;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      className="h-full flex items-center justify-center p-6"
    >
      <div className="glass-strong rounded-3xl p-8 md:p-10 max-w-md w-full text-center"
        style={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.04) inset, 0 40px 80px rgba(0,0,0,0.6)' }}
      >
        <div className="w-16 h-16 rounded-full bg-gold-glow border border-gold-dim flex items-center justify-center mx-auto mb-5">
          <Sparkles size={28} className="text-gold" />
        </div>

        <h2 className="font-display text-2xl text-cream mb-1">Quiz Complete!</h2>
        <p className="text-sm text-text mb-6">Here's how you performed:</p>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="glass p-3 rounded-xl">
            <p className="font-mono text-lg text-cream">{score}/{total}</p>
            <p className="text-[10px] text-text-dim uppercase tracking-widest mt-0.5">Correct</p>
          </div>
          <div className="glass p-3 rounded-xl">
            <p className="font-mono text-lg text-gold-text">{accuracy}%</p>
            <p className="text-[10px] text-text-dim uppercase tracking-widest mt-0.5">Accuracy</p>
          </div>
          <div className="glass p-3 rounded-xl">
            <p className="font-mono text-lg text-green">+{xpDisplay}</p>
            <p className="text-[10px] text-text-dim uppercase tracking-widest mt-0.5">XP</p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onRetry}
            className="flex-1 py-3 rounded-xl border border-border bg-surface text-text-hi hover:bg-surface-1 transition-all duration-150 text-sm font-medium flex items-center justify-center gap-2"
          >
            <RotateCcw size={16} />
            Try Again
          </button>
        </div>
      </div>
    </motion.div>
  );
};
